import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { SystemStateMeta } from "@/features/system-states/system-state";

/** Anchor styled as a button — for CTAs that navigate to a real, built route. */
export function ActionLink({
  href,
  children,
  variant = "primary",
  className,
}: {
  href: string;
  children: React.ReactNode;
  variant?: "primary" | "secondary";
  className?: string;
}) {
  return (
    <Link to={href} className={cn(buttonVariants({ variant }), className)}>
      {children}
    </Link>
  );
}

/** Shared page frame for the System State specimens (SYS-00 … SYS-06). */
export function SystemStatePage({
  meta,
  children,
}: {
  meta: SystemStateMeta;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-6">
      <header>
        <Link
          to="/system-states"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
        >
          <ArrowLeft aria-hidden className="size-3.5" /> System States
        </Link>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <h1 className="text-[24px] font-bold leading-tight tracking-tight text-ink">{meta.title}</h1>
          <span className="rounded-full bg-disabled-soft px-2 py-0.5 font-mono text-[11px] font-bold text-muted">
            {meta.id}
          </span>
        </div>
        <p className="mt-2 max-w-[780px] text-sm leading-relaxed text-muted">{meta.purpose}</p>
        <p className="mt-2 max-w-[780px] text-xs leading-relaxed text-subtle">
          <span className="font-semibold text-muted">Disposition:</span> {meta.disposition}
        </p>
      </header>
      {children}
      <footer className="border-t border-line pt-4 text-xs text-subtle">
        {meta.id} · EVIDENCE_CLASS PROTOTYPE_BACKED · no dedicated Figma frame · behaviour and copy follow the
        validated HTML prototype.
      </footer>
    </div>
  );
}

export function SpecimenSection({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("flex flex-col gap-3", className)}>
      <h2 className="border-b border-line pb-2 text-[11px] font-bold uppercase tracking-[0.06em] text-muted">
        {label}
      </h2>
      {children}
    </section>
  );
}

export function SpecimenCard({
  label,
  children,
  className,
}: {
  label?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("rounded-lg border border-line bg-surface p-5", className)}>
      {label && <div className="mb-3 text-xs font-semibold text-muted">{label}</div>}
      {children}
    </div>
  );
}

/**
 * Primary/secondary action for a module that is registered but not yet built.
 * Renders as a non-navigating affordance so the specimen never produces a dead route.
 */
export function CrossModuleLink({
  children,
  reason = "Available once the linked module is built",
  variant = "primary",
  className,
}: {
  children: React.ReactNode;
  reason?: string;
  variant?: "primary" | "secondary";
  className?: string;
}) {
  return (
    <span
      role="link"
      aria-disabled="true"
      title={reason}
      className={cn(
        "inline-flex min-h-[36px] cursor-not-allowed items-center justify-center gap-1.5 rounded-sm px-3.5 text-sm font-semibold opacity-60",
        variant === "primary" ? "bg-primary text-on-primary" : "border border-line-strong bg-surface text-ink",
        className
      )}
    >
      {children}
    </span>
  );
}
